<template>
  <div class="flex flex-col gap-8">
    <div class="flex flex-col gap-4">
      <div class="min-w-0 flex-1">
        <h3 class="text-display-small mb-1">{{ $t('common.objective', 2) }}</h3>
        <template v-if="canCover">
          <p class="text-label-large text-on-surface-variant">
            {{ $t('objective.progress', { n: coveredReviewCount, m: reviewDenominator }) }}
          </p>
          <BaseLinearProgressIndictor class="mt-1 w-full max-w-md" :percentage="progressPercent" />
        </template>
      </div>
    </div>

    <TransitionGroup tag="div" name="objective-list" class="relative flex flex-col gap-3">
      <h3 v-if="proposals.length" key="heading-proposals" class="text-title-medium">
        {{ $t('objective.impulse-list') }}
      </h3>
      <ObjectiveReviewItem
        v-for="objective in proposals"
        :key="objective.id"
        :objective="objective"
        section="proposals"
        :dialog-id="dialogId"
        :dialog-phase="dialogPhase"
        :feedback-topics="feedbackTopics"
        :feedback-topic="topicFor(objective)"
        :can-cover="canCover"
        :is-read-only="areImpulsesReadOnly || isReadOnly"
      />

      <h3
        v-if="runningObjectives.length"
        key="heading-running"
        class="text-title-medium"
        :class="{ 'mt-5': proposals.length > 0 }"
      >
        {{ $t('objective.ongoing') }}
      </h3>
      <ObjectiveReviewItem
        v-for="objective in runningObjectives"
        :key="objective.id"
        :objective="objective"
        section="running"
        :dialog-id="dialogId"
        :dialog-phase="dialogPhase"
        :feedback-topics="feedbackTopics"
        :feedback-topic="topicFor(objective)"
        :can-cover="canCover"
        :is-read-only="isReadOnly"
      />

      <h3
        key="heading-current"
        class="text-title-medium"
        :class="{ 'mt-5': proposals.length > 0 || runningObjectives.length > 0 }"
      >
        {{ $t('objective.current') }}
      </h3>
      <ObjectiveReviewItem
        v-for="objective in agreedObjectives"
        :key="objective.id"
        :objective="objective"
        section="agreed"
        :dialog-id="dialogId"
        :dialog-phase="dialogPhase"
        :feedback-topics="feedbackTopics"
        :feedback-topic="topicFor(objective)"
        :can-cover="canCover"
        :is-read-only="isReadOnly"
      />
      <div
        v-if="!agreedObjectives.length"
        key="agreed-empty"
        class="bg-surface-container-low rounded-large flex max-w-3xl flex-col items-center justify-center p-4"
      >
        <div class="relative h-16 w-16">
          <BaseShape shape="6-sided-cookie" class="text-primary-container h-full w-full" />
          <BaseIcon
            size="32"
            class="text-on-primary-container absolute top-1/2 left-1/2 z-1 -translate-x-1/2 -translate-y-1/2"
            >target</BaseIcon
          >
        </div>
        <h3 class="text-body-large text-on-surface-variant">
          {{ $t('objective.currentEmpty') }}
        </h3>
      </div>
      <BaseTextButton
        v-if="canCreateObjective"
        key="create-objective"
        color="outlined"
        icon="add"
        size="M"
        class="w-full max-w-3xl"
        shape="square"
        @click="$emit('create:objective')"
        >{{ $t('objective.create.action') }}</BaseTextButton
      >
    </TransitionGroup>

    <BaseDialog
      type="basic"
      :title="$t('objective.create.title')"
      :description="$t('objective.create.description')"
      :is-open="showForm"
      class="md:min-w-125"
      @close="$emit('cancel:create')"
    >
      <div class="flex flex-col gap-1">
        <BaseInputField
          type="text"
          required
          v-model="creatableObjective.title"
          :label="$t('objective.create.name')"
        />
        <BaseInputField
          type="multiText"
          v-model="creatableObjective.description"
          :label="$t('objective.create.descriptionLabel')"
        />
        <BaseInputSelect
          v-model="creatableObjective.feedbackTopicId"
          :label="$t('objective.create.feedbackTopic')"
          :items="[
            {
              id: undefined,
              title: t('objective.create.noFeedbackTopic'),
              selected: !creatableObjective.feedbackTopicId,
              leadingIcon: 'remove',
            },
            ...selectableFeedbackTopics.map((feedbackTopic) => ({
              id: feedbackTopic.id,
              title: feedbackTopic.title,
              leadingIcon: feedbackTopic.icon,
              selected: feedbackTopic.id === creatableObjective.feedbackTopicId,
            })),
          ]"
        />
      </div>
      <template #dismiss>
        <BaseTextButton color="text" size="M" @click="$emit('cancel:create')">{{
          $t('common.cancel')
        }}</BaseTextButton>
      </template>
      <template #confirm>
        <BaseTextButton
          color="filled"
          size="M"
          @click="$emit('save:objective')"
          :disabled="!creatableObjective.title"
        >
          {{ $t('objective.create.action') }}</BaseTextButton
        >
      </template>
    </BaseDialog>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue'
  import { useI18n } from 'vue-i18n'
  import type { CreateableObjective, Objective } from '@/core/composables/use-objectives'
  import { withoutCoachOnlyTopics, type FeedbackTopic } from '@/core/models/feedback-topic'
  import { DialogPhase } from '@/core/models/dialog/dialog'
  import ObjectiveReviewItem from '@/core/components/objective/ObjectiveReviewItem.vue'
  import BaseTextButton from '@/core/components/base/button/text-button/BaseTextButton.vue'
  import BaseInputField from '@/core/components/base/form-fields/BaseInputField.vue'
  import BaseInputSelect from '@/core/components/base/form-fields/BaseInputSelect.vue'
  import BaseDialog from '@/core/components/base/dialog/BaseDialog.vue'
  import BaseShape from '@/core/components/base/shapes/BaseShape.vue'
  import BaseIcon from '@/core/components/base/icon/BaseIcon.vue'
  import BaseLinearProgressIndictor from '@/core/components/base/linear-progress-indicator/BaseLinearProgressIndictor.vue'

  interface Props {
    proposals: ReadonlyArray<Objective>
    runningObjectives: ReadonlyArray<Objective>
    agreedObjectives: ReadonlyArray<Objective>
    dialogId: string
    creatableObjective: Partial<CreateableObjective>
    showForm: boolean
    isReadOnly?: boolean
    canCreateObjective: boolean
    canCover: boolean
    dialogPhase: DialogPhase
    areImpulsesReadOnly?: boolean
    feedbackTopics: ReadonlyArray<FeedbackTopic>
    coveredReviewCount: number
    reviewDenominator: number
  }

  const props = defineProps<Props>()

  const { t } = useI18n()
  const selectableFeedbackTopics = computed(() => withoutCoachOnlyTopics(props.feedbackTopics))

  defineEmits<{
    (e: 'create:objective'): void
    (e: 'save:objective'): void
    (e: 'cancel:create'): void
  }>()

  const progressPercent = computed(() => {
    if (!props.reviewDenominator) return 0
    return Math.min(100, Math.round((props.coveredReviewCount / props.reviewDenominator) * 100))
  })

  const topicFor = (objective: Objective) =>
    props.feedbackTopics.find((topic) => topic.id === objective.feedbackTopicId)
</script>

<style scoped>
  .objective-list-move {
    transition: transform 450ms var(--ease-fast-effect);
  }

  .objective-list-enter-active,
  .objective-list-leave-active {
    transition: opacity 300ms ease;
  }

  .objective-list-enter-from,
  .objective-list-leave-to {
    opacity: 0;
  }

  .objective-list-leave-active {
    position: absolute;
    width: 100%;
    max-width: 48rem;
  }

  @media (prefers-reduced-motion: reduce) {
    .objective-list-move,
    .objective-list-enter-active,
    .objective-list-leave-active {
      transition: none;
    }
  }
</style>
